import { useState, useCallback, useRef } from 'react';

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number; // Time to live in milliseconds
}

interface UseCacheOptions {
  ttl?: number; // Default: 5 minutes
  maxSize?: number; // Default: 100 entries
}

export function useCache<T = any>(options: UseCacheOptions = {}) {
  const { ttl = 5 * 60 * 1000, maxSize = 100 } = options; // 5 minutes default
  
  // Use a Map for better performance than object
  const cacheRef = useRef<Map<string, CacheEntry<T>>>(new Map());
  const [, forceUpdate] = useState({});

  const isExpired = useCallback((entry: CacheEntry<T>) => {
    return Date.now() - entry.timestamp > entry.ttl;
  }, []);

  const cleanup = useCallback(() => {
    const cache = cacheRef.current;
    const now = Date.now();
    
    // Remove expired entries
    for (const [key, entry] of cache.entries()) {
      if (now - entry.timestamp > entry.ttl) {
        cache.delete(key);
      }
    }
    
    // If cache is too large, remove oldest entries
    if (cache.size > maxSize) {
      const entries = Array.from(cache.entries())
        .sort((a, b) => a[1].timestamp - b[1].timestamp);
      
      const toDelete = entries.slice(0, cache.size - maxSize);
      toDelete.forEach(([key]) => cache.delete(key));
    }
  }, [maxSize]);

  const get = useCallback((key: string): T | null => {
    const cache = cacheRef.current;
    const entry = cache.get(key);
    
    if (!entry) {
      return null;
    }
    
    if (isExpired(entry)) {
      cache.delete(key);
      return null;
    }
    
    return entry.data;
  }, [isExpired]);

  const set = useCallback((key: string, data: T, customTtl?: number) => {
    const cache = cacheRef.current;
    const entry: CacheEntry<T> = {
      data,
      timestamp: Date.now(),
      ttl: customTtl || ttl,
    };
    
    cache.set(key, entry);
    cleanup();
    
    // Force re-render if needed
    forceUpdate({});
  }, [ttl, cleanup]);

  const remove = useCallback((key: string) => {
    cacheRef.current.delete(key);
  }, []);

  const clear = useCallback(() => {
    cacheRef.current.clear();
    forceUpdate({});
  }, []);

  const has = useCallback((key: string): boolean => {
    const entry = cacheRef.current.get(key);
    if (!entry) return false;
    
    if (isExpired(entry)) {
      cacheRef.current.delete(key);
      return false;
    }
    
    return true;
  }, [isExpired]);

  const getCacheStats = useCallback(() => {
    const cache = cacheRef.current;
    const now = Date.now();
    let expired = 0;
    
    for (const entry of cache.values()) {
      if (now - entry.timestamp > entry.ttl) {
        expired++;
      }
    }
    
    return {
      size: cache.size,
      maxSize,
      expired,
      ttl,
    };
  }, [maxSize, ttl]);

  return {
    get,
    set,
    remove,
    clear,
    has,
    getCacheStats,
    cleanup,
  };
}
